/**
 * A loudness meter on an audio node. `read()` returns 0 to 1, with a fast
 * attack and a slower release so it reads like a voice, not a waveform.
 */
export interface Meter {
  input: AudioNode;
  read: () => number;
}

export const createMeter = (ctx: AudioContext): Meter => {
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  analyser.smoothingTimeConstant = 0;
  // Some browsers only process nodes that reach the destination; route
  // through a muted gain so the meter always runs and nothing is heard twice.
  const sink = ctx.createGain();
  sink.gain.value = 0;
  analyser.connect(sink).connect(ctx.destination);

  const buf = new Float32Array(analyser.fftSize);
  let level = 0;
  let last = 0;

  return {
    input: analyser,
    read() {
      const now = performance.now();
      // One reading per frame is plenty; repeat callers get the cached value.
      if (now - last < 8) return level;
      last = now;
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i]! * buf[i]!;
      const rms = Math.sqrt(sum / buf.length);
      // Speech sits roughly between 0.01 and 0.3 RMS; map it onto a soft 0..1 curve.
      const target = 1 - Math.exp(-rms * 14);
      level += (target - level) * (target > level ? 0.6 : 0.12);
      return level;
    },
  };
};
